import { CaretLeftIcon, CaretRightIcon, FilePdfIcon, XIcon } from '@phosphor-icons/react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { pdfjs, Document as PdfDocument, Page as PdfPage } from 'react-pdf';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import { Button } from 'src/components/button/Button';
import { constructDownloadUrl } from 'src/app/document/models';
import type { TrailerCheckPhotoResponse } from '../endpoints/TrailerCheckEndpoint.types';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface TrailerCheckPhotoGalleryProps {
  photos: TrailerCheckPhotoResponse[];
  viewerPhotos?: TrailerCheckPhotoResponse[];
}

type TrailerCheckViewerPage = {
  key: string;
  photo: TrailerCheckPhotoResponse;
  url: string;
  type: 'image' | 'pdf';
  pageNumber: number;
  pageCount?: number;
};

function isPdfFileName(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf');
}

function getFirstPageKey(photo: TrailerCheckPhotoResponse): string {
  return isPdfFileName(photo.documentName) ? `${photo.id}:page:1` : `${photo.id}:image`;
}

export function TrailerCheckPhotoGallery({ photos, viewerPhotos }: TrailerCheckPhotoGalleryProps) {
  const navigationPhotos = useMemo(
    () => (viewerPhotos && viewerPhotos.length > 0 ? viewerPhotos : photos),
    [photos, viewerPhotos],
  );
  const [pdfPageCounts, setPdfPageCounts] = useState<Record<string, number>>({});
  const [selectedPageKey, setSelectedPageKey] = useState<string | null>(null);
  const viewerPages = useMemo<TrailerCheckViewerPage[]>(() => {
    return navigationPhotos.flatMap((photo) => {
      const url = constructDownloadUrl({ documentId: photo.documentId, documentHash: photo.documentHash });
      if (!isPdfFileName(photo.documentName)) {
        const imagePage: TrailerCheckViewerPage = {
          key: getFirstPageKey(photo),
          photo,
          url,
          type: 'image',
          pageNumber: 1,
        };
        return [imagePage];
      }

      const pageCount = pdfPageCounts[photo.id];
      const pages = Array.from({ length: pageCount ?? 1 }, (_, index) => index + 1);
      return pages.map<TrailerCheckViewerPage>((pageNumber) => ({
        key: `${photo.id}:page:${pageNumber}`,
        photo,
        url,
        type: 'pdf',
        pageNumber,
        pageCount,
      }));
    });
  }, [navigationPhotos, pdfPageCounts]);
  const selectedIndex = selectedPageKey ? viewerPages.findIndex((page) => page.key === selectedPageKey) : -1;
  const selectedPage = selectedIndex >= 0 ? viewerPages[selectedIndex] : null;

  const closeGallery = useCallback(() => setSelectedPageKey(null), []);
  const handlePdfLoadSuccess = useCallback((photoId: string, pageCount: number) => {
    setPdfPageCounts((current) => (current[photoId] === pageCount ? current : { ...current, [photoId]: pageCount }));
  }, []);

  const showPrevious = useCallback(() => {
    setSelectedPageKey((current) => {
      if (current === null || viewerPages.length === 0) return current;
      const currentIndex = viewerPages.findIndex((page) => page.key === current);
      if (currentIndex < 0) return current;
      return viewerPages[(currentIndex - 1 + viewerPages.length) % viewerPages.length].key;
    });
  }, [viewerPages]);

  const showNext = useCallback(() => {
    setSelectedPageKey((current) => {
      if (current === null || viewerPages.length === 0) return current;
      const currentIndex = viewerPages.findIndex((page) => page.key === current);
      if (currentIndex < 0) return current;
      return viewerPages[(currentIndex + 1) % viewerPages.length].key;
    });
  }, [viewerPages]);

  useEffect(() => {
    if (!selectedPage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNext();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [selectedPage, showNext, showPrevious]);

  if (photos.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
        {photos.map((photo) => {
          const url = constructDownloadUrl({ documentId: photo.documentId, documentHash: photo.documentHash });
          const isPdf = isPdfFileName(photo.documentName);

          if (isPdf) {
            return (
              <button
                key={photo.id}
                type="button"
                className="flex shrink-0 snap-start flex-col items-center justify-center w-32 h-32 border border-border rounded bg-surface-sunken hover:bg-surface-hover gap-1 p-2"
                onClick={() => setSelectedPageKey(getFirstPageKey(photo))}
                title={photo.documentName}
              >
                <FilePdfIcon className="w-8 h-8 text-fg-muted" />
                <span className="text-xs text-fg-muted text-center truncate w-full">{photo.documentName}</span>
              </button>
            );
          }

          return (
            <button
              key={photo.id}
              type="button"
              className="block shrink-0 snap-start w-32 h-32 border border-border rounded overflow-hidden hover:opacity-80"
              onClick={() => setSelectedPageKey(getFirstPageKey(photo))}
            >
              <img src={url} alt={photo.documentName} className="w-full h-full object-cover" />
            </button>
          );
        })}
      </div>

      {selectedPage && (
        <RadixDialog.Root open={true} onOpenChange={(open) => !open && closeGallery()}>
          <RadixDialog.Portal>
            <RadixDialog.Overlay className="fixed inset-0 z-dialog bg-surface-inverse/70" />
            <RadixDialog.Content className="fixed inset-4 z-dialog flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-lg focus:outline-hidden md:inset-8">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <RadixDialog.Title className="heading-four truncate">
                    {selectedPage.photo.documentName}
                  </RadixDialog.Title>
                  <div className="text-sm text-fg-muted">
                    Pagina {selectedIndex + 1} / {viewerPages.length}
                    {selectedPage.type === 'pdf' && (
                      <span>
                        {' '}
                        &middot; PDF pagina {selectedPage.pageNumber}
                        {selectedPage.pageCount ? ` / ${selectedPage.pageCount}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-4 text-sm">
                  <Button color="secondary" onTrigger={closeGallery} iconLeft={<XIcon className="button-icon" />}>
                    Sluiten
                  </Button>
                </div>
              </div>

              <div className="relative min-h-0 flex-1">
                {navigationPhotos.map((photo) => {
                  const url = constructDownloadUrl({ documentId: photo.documentId, documentHash: photo.documentHash });
                  const isActive = selectedPage.photo.id === photo.id;

                  if (isPdfFileName(photo.documentName)) {
                    return (
                      <PdfPhotoView
                        key={photo.id}
                        photo={photo}
                        url={url}
                        isActive={isActive}
                        activePageNumber={isActive ? selectedPage.pageNumber : null}
                        onLoadSuccess={handlePdfLoadSuccess}
                      />
                    );
                  }

                  return <ImagePhotoView key={photo.id} photo={photo} url={url} isActive={isActive} />;
                })}

                <button
                  type="button"
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-md border border-border bg-surface/90 p-2 text-fg-secondary shadow-md hover:bg-surface hover:text-fg"
                  onClick={showPrevious}
                  aria-label="Vorige pagina"
                >
                  <CaretLeftIcon className="w-6 h-6" />
                </button>

                <button
                  type="button"
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-md border border-border bg-surface/90 p-2 text-fg-secondary shadow-md hover:bg-surface hover:text-fg"
                  onClick={showNext}
                  aria-label="Volgende pagina"
                >
                  <CaretRightIcon className="w-6 h-6" />
                </button>
              </div>
            </RadixDialog.Content>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      )}
    </>
  );
}

// Stays mounted for every PDF attachment while the viewer is open so each PDF is
// fetched once up front (preloaded) and navigation between pages stays instant.
// When inactive we keep the document loaded but render no page.
function PdfPhotoView({
  photo,
  url,
  isActive,
  activePageNumber,
  onLoadSuccess,
}: {
  photo: TrailerCheckPhotoResponse;
  url: string;
  isActive: boolean;
  activePageNumber: number | null;
  onLoadSuccess: (photoId: string, pageCount: number) => void;
}) {
  return (
    <div
      className={
        isActive ? 'absolute inset-0 flex overflow-auto rounded border border-border bg-surface-sunken' : 'hidden'
      }
    >
      <div className="m-auto">
        <PdfDocument
          file={url}
          loading={<div className="p-4 text-fg-muted">PDF pagina laden...</div>}
          error={
            <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
              <FilePdfIcon className="w-8 h-8 text-fg-muted" />
              <span>PDF pagina kan niet worden geladen.</span>
              <a href={url} target="_blank" rel="noopener noreferrer" className="link-text">
                Open PDF in nieuw tabblad
              </a>
            </div>
          }
          onLoadSuccess={({ numPages }: { numPages: number }) => onLoadSuccess(photo.id, numPages)}
        >
          {isActive && activePageNumber != null && (
            <PdfPage pageNumber={activePageNumber} renderAnnotationLayer={false} renderTextLayer={false} width={900} />
          )}
        </PdfDocument>
      </div>
    </div>
  );
}

function ImagePhotoView({
  photo,
  url,
  isActive,
}: {
  photo: TrailerCheckPhotoResponse;
  url: string;
  isActive: boolean;
}) {
  return (
    <div
      className={
        isActive
          ? 'absolute inset-0 flex items-center justify-center overflow-auto rounded border border-border bg-surface-sunken'
          : 'hidden'
      }
    >
      <img src={url} alt={photo.documentName} className="max-h-full max-w-full object-contain" />
    </div>
  );
}
